import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAdminApi, errMsg, useSite } from "@/lib/site";
import { Stars } from "@/components/site/Reviews";
import { AdminTitle } from "./AdminMessages";
import { Field, inputCls } from "./AdminPackages";

const BLANK = { name: "New client", role: "", service: "", rating: 5, text: "Write the client's review here.", active: false, sample: false, featured: false, order: 0 };

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm">{label}<Switch checked={checked} onCheckedChange={onChange} data-testid={testid} /></label>
);

const ReviewEditor = ({ r, packages, onChange }) => {
  const call = useAdminApi();
  const [f, setF] = useState(r);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, rating: Number(f.rating), order: Number(f.order) };
    delete body.id;
    try { await call("put", `/admin/reviews/${r.id}`, body); toast.success("Review saved"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete the review from "${r.name}"?`)) return;
    try { await call("delete", `/admin/reviews/${r.id}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-3xl bg-white border p-5 sm:p-6 space-y-3 ${f.active ? "" : "opacity-70"}`} data-testid={`admin-review-${r.id}`}>
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-3"><p className="font-display font-bold">{r.name}</p><Stars n={Number(f.rating)} /></div>
        <div className="flex flex-wrap gap-4">
          <Toggle label={f.active ? "Visible" : "Hidden"} checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid={`admin-review-active-${r.id}`} />
          <Toggle label="Featured" checked={f.featured} onChange={(v) => setF({ ...f, featured: v })} testid={`admin-review-featured-${r.id}`} />
          <Toggle label="Sample" checked={f.sample} onChange={(v) => setF({ ...f, sample: v })} testid={`admin-review-sample-${r.id}`} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Client name"><input className={inputCls} value={f.name} onChange={set("name")} data-testid={`admin-review-name-${r.id}`} /></Field>
        <Field label="Role / business"><input className={inputCls} value={f.role} onChange={set("role")} data-testid={`admin-review-role-${r.id}`} /></Field>
        <Field label="Service">
          <select className={inputCls} value={f.service} onChange={set("service")} data-testid={`admin-review-service-${r.id}`}>
            <option value="">General</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stars">
            <select className={inputCls} value={f.rating} onChange={set("rating")} data-testid={`admin-review-rating-${r.id}`}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Order"><input type="number" className={inputCls} value={f.order} onChange={set("order")} data-testid={`admin-review-order-${r.id}`} /></Field>
        </div>
      </div>
      <Field label="Review"><textarea rows={3} className={inputCls} value={f.text} onChange={set("text")} data-testid={`admin-review-text-${r.id}`} /></Field>
      <div className="flex justify-between">
        <button onClick={del} className="flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-testid={`admin-review-delete-${r.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
        <button onClick={save} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 text-sm font-semibold" data-testid={`admin-review-save-${r.id}`}>Save</button>
      </div>
    </div>
  );
};

export default function AdminReviews() {
  const call = useAdminApi();
  const { packages, reload } = useSite();
  const [list, setList] = useState(null);
  const load = useCallback(() => call("get", "/admin/reviews").then(setList).catch((e) => toast.error(errMsg(e))), [call]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); reload(); };
  const add = async () => {
    try { await call("post", "/admin/reviews", BLANK); toast.success("Review added — it stays hidden until you switch it to Visible"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = (list || []).filter((r) => r.active).length;
  return (
    <div data-testid="admin-reviews">
      <div className="flex items-start justify-between gap-3">
        <AdminTitle sub={`${shown} visible on the site. "Featured" picks the big quote on the About page. "Sample" adds a Sample label.`}>Reviews</AdminTitle>
        <button onClick={add} className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold shrink-0" data-testid="admin-review-add-btn"><Plus className="w-4 h-4" /> Add</button>
      </div>
      {list && !list.length && <p className="text-sm text-slate-500" data-testid="admin-reviews-empty">No reviews yet.</p>}
      <div className="space-y-4">
        {(list || []).map((r) => <ReviewEditor key={`${r.id}-${r.order}-${r.active}-${r.featured}`} r={r} packages={packages} onChange={changed} />)}
      </div>
    </div>
  );
}
